import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AuthSection from '../components/AuthSection.jsx';
import Icon from '../components/Icon.jsx';
import HistoryMetricsSection from '../components/history/HistoryMetricsSection.jsx';
import ToastContainer from '../components/ToastContainer.jsx';
import { useBootstrap } from '../context/BootstrapContext.jsx';
import { usePageStyles, useLucideIcons } from '../hooks/usePageAssets.js';
import { useRestaurants } from '../hooks/useRestaurants.js';
import { resolveStaticUrl } from '../utils/staticAssets.js';
import { exportDocumentFile, formatDateTime, groupDetailRecords } from '../utils/exportDocument.js';

const DEFAULT_PAGE_SIZE = 20;

const createDefaultDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  const localIso = `${year}-${month}-${day}`;
  return { from: localIso, to: localIso };
};

const parseParametros = (parametros) => {
  if (!parametros) {
    return { isManualTime: false, simulationTime: null };
  }
  try {
    const parsed = typeof parametros === 'string' ? JSON.parse(parametros) : parametros;
    return {
      isManualTime: Boolean(parsed?.is_manual_time),
      simulationTime: parsed?.simulation_time || null
    };
  } catch (error) {
    return { isManualTime: false, simulationTime: null };
  }
};

const HistoryHeroStats = ({ stats }) => (
  <section className="history-hero">
    <div className="hero-content">
      <div className="hero-icon">
        <i data-lucide="archive" width="28" height="28"></i>
      </div>
      <h1 className="hero-title">Historial de Pronósticos</h1>
      <p className="hero-subtitle">Consulta y gestiona el archivo completo de pronósticos generados</p>
    </div>
    <div className="hero-stats" id="heroStats">
      <div className="stat-card">
        <div className="stat-value" id="totalRecords">{stats.total.toLocaleString()}</div>
        <div className="stat-label">Registros Totales</div>
      </div>
      <div className="stat-card">
        <div className="stat-value" id="thisWeekCount">{stats.thisWeek.toLocaleString()}</div>
        <div className="stat-label">Esta Semana</div>
      </div>
      <div className="stat-card">
        <div className="stat-value" id="uniqueLocations">{stats.locations.toLocaleString()}</div>
        <div className="stat-label">Sucursales</div>
      </div>
    </div>
  </section>
);

const HistoryFilters = ({
  filters,
  searchDraft,
  sucursales,
  onFiltersChange,
  onReset,
  onSearchDraftChange,
  onSearchApply
}) => (
  <section className="filters-panel" id="historyFilters">
    <div className="filters-container">
      <div className="filters-header">
        <h2 className="filters-title">
          <i data-lucide="filter" width="18" height="18"></i>
          Filtros de Búsqueda
        </h2>
        <button className="filters-reset-btn" onClick={onReset}>
          <i data-lucide="rotate-ccw" width="16" height="16"></i>
          <span>Limpiar</span>
        </button>
      </div>

      <div className="filters-grid">
        <div className="filter-group">
          <label className="filter-label">
            <i data-lucide="building" width="16" height="16"></i>
            Sucursal
          </label>
          <select
            id="hist-sucursal"
            className="filter-select"
            value={filters.sucursal}
            onChange={(event) => onFiltersChange({ sucursal: event.target.value })}
          >
            <option value="">Todas las sucursales</option>
            {sucursales.map((sucursal) => (
              <option key={sucursal} value={sucursal}>{sucursal}</option>
            ))}
          </select>
        </div>

        <div className="filter-group date-range-group">
          <label className="filter-label">
            <i data-lucide="calendar" width="16" height="16"></i>
            Rango de Fechas
          </label>
          <div className="date-inputs">
            <input
              type="date"
              id="hist-from"
              className="filter-date"
              value={filters.from}
              onChange={(event) => onFiltersChange({ from: event.target.value })}
            />
            <span className="date-separator">–</span>
            <input
              type="date"
              id="hist-to"
              className="filter-date"
              value={filters.to}
              onChange={(event) => onFiltersChange({ to: event.target.value })}
            />
          </div>
        </div>

        <div className="filter-group search-group">
          <label className="filter-label">
            <i data-lucide="search" width="16" height="16"></i>
            Búsqueda Global
          </label>
          <div className="search-input-wrapper">
            <input
              type="text"
              id="hist-q"
              className="filter-search"
              placeholder="Folio, usuario, sucursal..."
              value={searchDraft}
              onChange={(event) => onSearchDraftChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onSearchApply();
                }
              }}
            />
            <button className="search-btn" id="hist-search" onClick={onSearchApply}>
              <i data-lucide="search" width="16" height="16"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HistoryResultsHeader = ({ viewMode, onViewChange, onRefresh, totalRecords, refreshing }) => (
  <div className="results-header">
    <div className="results-info">
      <h2 className="results-title">Resultados</h2>
      <div className="results-count" id="resultsCount">
        <span className="count-number">{totalRecords}</span>
        <span className="count-label">registros encontrados</span>
      </div>
    </div>
    <div className="results-controls">
      <div className="view-toggle">
        <span className="view-toggle-label">
          <i data-lucide="layout-panel-left" width="14" height="14"></i>
          <span>Vista</span>
        </span>
        <button
          className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
          data-view="table"
          id="tableViewBtn"
          onClick={() => onViewChange('table')}
        >
          <i data-lucide="list" width="16" height="16"></i>
          <span>Tabla</span>
        </button>
        <button
          className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
          data-view="cards"
          id="cardViewBtn"
          onClick={() => onViewChange('cards')}
        >
          <i data-lucide="grid-3x3" width="16" height="16"></i>
          <span>Tarjetas</span>
        </button>
      </div>
      <button
        className="refresh-btn"
        id="refreshBtn"
        onClick={onRefresh}
        disabled={refreshing}
        aria-busy={refreshing}
      >
        <i data-lucide="refresh-cw" width="16" height="16"></i>
        <span>Actualizar</span>
      </button>
    </div>
  </div>
);

const HistoryCardsView = ({ records, onAction }) => (
  <div className="cards-view" id="cardsView" style={{ display: records.length ? 'block' : 'none' }}>
    <div className="cards-grid" id="cardsGrid">
      {records.map((record) => (
        <div className="history-card" key={record.id} data-id={record.id}>
          <div className="card-header">
            <div className="card-folio">FH-{record.id}</div>
            <div className="card-date">
              {record.isManualTime && record.simulationTime ? (
                <>
                  <div className="time-row">
                    <i data-lucide="clock" width="14" height="14"></i>
                    <span className="time-label">Generado:</span>
                    <span>{formatDateTime(record.created_at)}</span>
                  </div>
                  <div className="time-row manual-time">
                    <i data-lucide="calendar-clock" width="14" height="14"></i>
                    <span className="time-label">Data de:</span>
                    <span>{formatDateTime(record.simulationTime)}</span>
                  </div>
                </>
              ) : (
                formatDateTime(record.created_at)
              )}
            </div>
          </div>
          <div className="card-content">
            <div className="card-location">
              <i data-lucide="building" width="16" height="16"></i>
              <span>{record.sucursal_nombre || 'Sin sucursal'}</span>
            </div>
            <div className="card-user">
              <i data-lucide="user" width="16" height="16"></i>
              <span>{record.user_name || record.user_email || 'Usuario desconocido'}</span>
            </div>
          </div>
          <div className="card-actions">
            <button className="card-action-btn primary" onClick={() => onAction('view', record.id)}>
              <i data-lucide="eye" width="16" height="16"></i>
              <span>Ver Detalle</span>
            </button>
            <button className="card-action-btn" onClick={() => onAction('pdf', record.id)}>
              <i data-lucide="file-text" width="16" height="16"></i>
              <span>PDF</span>
            </button>
            <button className="card-action-btn" onClick={() => onAction('jpg', record.id)}>
              <i data-lucide="image" width="16" height="16"></i>
              <span>JPG</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const HistoryTableView = ({ records, onAction }) => (
  <div className="table-view" id="tableView" style={{ display: records.length ? 'block' : 'none' }}>
    <div className="table-container">
      <table className="history-table">
        <thead>
          <tr>
            <th className="th-date">
              <button className="sort-btn" data-sort="date">
                <span>Fecha</span>
                <i data-lucide="chevron-up-down" width="14" height="14"></i>
              </button>
            </th>
            <th className="th-location">Sucursal</th>
            <th className="th-user">Usuario</th>
            <th className="th-folio">Folio</th>
            <th className="th-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={record.id}>
              <td>
                {record.isManualTime && record.simulationTime ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <i data-lucide="clock" width="12" height="12"></i>
                      <span style={{ color: 'var(--text-muted)' }}>Generado:</span>
                      <strong>{formatDateTime(record.created_at)}</strong>
                    </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--always-red)' }}>
                    <i data-lucide="calendar-clock" width="12" height="12"></i>
                    <span>Data de:</span>
                    <strong>{formatDateTime(record.simulationTime)}</strong>
                  </div>
                </div>
                ) : (
                  formatDateTime(record.created_at)
                )}
              </td>
              <td>{record.sucursal_nombre || '-'}</td>
              <td>{record.user_name || record.user_email || '-'}</td>
              <td><strong>FH-{record.id}</strong></td>
              <td>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button className="card-action-btn primary" onClick={() => onAction('view', record.id)}>
                    <i data-lucide="eye" width="14" height="14"></i>
                    Ver
                  </button>
                  <button className="card-action-btn" onClick={() => onAction('pdf', record.id)}>
                    <i data-lucide="file-text" width="14" height="14"></i>
                    PDF
                  </button>
                  <button className="card-action-btn" onClick={() => onAction('jpg', record.id)}>
                    <i data-lucide="image" width="14" height="14"></i>
                    JPG
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const PaginationControls = ({ page, total, pageSize, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const visibleCount = 5;
  let startPage = Math.max(1, page - Math.floor(visibleCount / 2));
  let endPage = Math.min(totalPages, startPage + visibleCount - 1);
  if (endPage - startPage + 1 < visibleCount) {
    startPage = Math.max(1, endPage - visibleCount + 1);
  }

  return (
    <section className="table-pagination">
      <div className="pagination-container">
        <button
          className="pagination-btn"
          id="hist-prev"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <i data-lucide="chevron-left" width="16" height="16"></i>
        </button>
        <div className="pagination-info" id="paginationInfo">
          <div className="page-details">
            Página {page} de {totalPages} • {total.toLocaleString()} registros
          </div>
          <div className="page-numbers" id="pageNumbers">
            {Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index).map((pageNumber) => (
              <button
                key={pageNumber}
                className={`page-btn ${pageNumber === page ? 'active' : ''}`}
                onClick={() => onPageChange(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
          </div>
        </div>
        <button
          className="pagination-btn"
          id="hist-next"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <i data-lucide="chevron-right" width="16" height="16"></i>
        </button>
      </div>
    </section>
  );
};

const HistoryDetailModal = ({ open, loading, header, detailGroups, onClose }) => {
  if (!open) {
    return null;
  }

  return (
    <div className="modal" id="histDetailModal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <div>
            <h3 id="histDetailTitle">{header ? `FH-${header.id}` : 'FH-…'}</h3>
            {header && (
              <p id="histDetailSubtitle">
                <span style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>{header.sucursal_nombre || 'Sin sucursal'}</span>
                  <span>•</span>
                  <span>{formatDateTime(header.created_at)}</span>
                  {header?.user_name || header?.user_email ? (
                    <>
                      <span>•</span>
                      <span>{header.user_name || header.user_email}</span>
                    </>
                  ) : null}
                </span>
              </p>
            )}
          </div>
          <button className="modal-close" id="histDetailClose" onClick={onClose}>
            <i data-lucide="x" width="16" height="16"></i>
          </button>
        </div>
        <div className="modal-body" id="histDetailBody">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner" />
              <div className="loading-text">Cargando detalle…</div>
            </div>
          )}
          {!loading && detailGroups && detailGroups.groups.length > 0 && (
            <>
              {detailGroups.groups.map((group) => (
                <div className="modal-group" key={group.key}>
                  <h3 className="export-group-title">
                    {detailGroups.isRecipe ? 'Artículo vinculado' : 'Categoría'}: {group.key}
                  </h3>
                  <table className="export-table">
                    <thead>
                      <tr>
                        <th className="center" style={{ width: '48px' }}>Img</th>
                        <th>Producto</th>
                        <th style={{ width: '110px' }}>Vida útil</th>
                        <th style={{ width: '130px' }}>Forecast</th>
                        <th style={{ width: '150px' }}>Horario forecast</th>
                        <th style={{ width: '110px' }}>Unidad</th>
                        <th className="qty" style={{ width: '120px' }}>Pronosticado</th>
                        <th className="qty" style={{ width: '120px' }}>Stock</th>
                        <th className="qty" style={{ width: '120px' }}>A Producir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows
                        .filter((row) => row.kind === 'ingredients' || row._quantity > 0)
                        .map((row, index) => {
                          if (row.kind === 'ingredients') {
                            const ingredientsText = (row.ingredients || [])
                              .map((ingredient) => `${ingredient.name}: ${ingredient.displayQuantity} ${ingredient.unitLabel}`)
                              .join(' · ');
                            return (
                              <tr className="export-ingredient-row" key={`${group.key}-ingredients-${index}`}>
                                <td colSpan={9}>
                                  <span className="export-ingredient-label">Ingredientes</span>
                                  <span className="export-ingredient-text">{ingredientsText || '-'}</span>
                                </td>
                              </tr>
                            );
                          }
                          const isAzucarGlass = Boolean(row.isAzucarGlass);
                          const unitLabel = (row.unidad_articulo || row.unidades_articulo || 'UNIDADES').toString().toUpperCase();
                          const forecastValue = Number.isFinite(Number(row.cantidad_pronosticada))
                            ? (isAzucarGlass ? Number(row.cantidad_pronosticada).toFixed(2) : Math.round(row.cantidad_pronosticada))
                            : '';
                          const stockValue = row.stock_capturado !== null && row.stock_capturado > 0 ? row.stock_capturado : '';
                          const toProduceValue = isAzucarGlass
                            ? (Number.isFinite(Number(row._quantity)) ? Number(row._quantity).toFixed(2) : '')
                            : (Number.isFinite(Number(row._quantity)) ? Math.round(row._quantity) : '');
                          return (
                            <tr key={`${group.key}-${row.entidad_id}-${index}`} className={row.kind === 'recipe' ? 'export-recipe-row' : ''}>
                              <td className="center">
                                <img
                                  className="export-thumb"
                                  src={row.imagen_url || '/static/images/placeholder.png'}
                                  alt={row.entidad_nombre || ''}
                                  style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '6px' }}
                                />
                              </td>
                              <td>
                                {row.kind === 'recipe' ? (
                                  <>
                                    <div className="export-recipe-name">{row.entidad_nombre || ''}</div>
                                    <div className="export-recipe-base">Base: {row.base_nombre || ''}</div>
                                  </>
                                ) : (
                                  row.entidad_nombre || ''
                                )}
                              </td>
                              <td>{row.lifespanText || '-'}</td>
                              <td>{row.coverageSpanText || '-'}</td>
                              <td>{row.coverageRangeText || '-'}</td>
                              <td>{unitLabel}</td>
                              <td className="qty"><strong>{forecastValue}</strong></td>
                              <td className="qty"><strong>{stockValue}</strong></td>
                              <td className="qty"><strong>{toProduceValue}</strong></td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  <div className="export-totals">
                    Subtotal {group.key}:{' '}
                    {detailGroups.isAzucarGlass
                      ? Number(group.subtotal || 0).toFixed(2)
                      : Math.round(group.subtotal || 0)}
                  </div>
                </div>
              ))}
              <div className="export-totals" style={{ marginTop: '24px' }}>
                <strong>
                  TOTAL UNIDADES:{' '}
                  {detailGroups.isAzucarGlass ? Number(detailGroups.total || 0).toFixed(2) : Math.round(detailGroups.total || 0)}
                </strong>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="card-action-btn" id="histDetailOk" onClick={onClose}>
            <i data-lucide="check" width="16" height="16"></i>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const HistoryPage = () => {
  const bootstrap = useBootstrap();
  usePageStyles([resolveStaticUrl('css/style.css'), resolveStaticUrl('css/history-time-display.css')]);
  const { count: restaurantsCount } = useRestaurants();
  
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef(new Map());
  
  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = toastTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimers.current.delete(id);
    }
  }, []);
  
  const pushToast = useCallback((message, type = 'info') => {
    if (!message) {
      return;
    }
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    const timer = setTimeout(() => dismissToast(id), 4000);
    toastTimers.current.set(id, timer);
  }, [dismissToast]);
  
  const showDashboard = restaurantsCount > 1;
  const [activeTab, setActiveTab] = useState('historial'); // 'historial' or 'metricas'
  const [filters, setFilters] = useState(() => ({
    sucursal: '',
    query: '',
    ...createDefaultDateRange()
  }));
  const [searchDraft, setSearchDraft] = useState(filters.query);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [viewMode, setViewMode] = useState('cards');
  const [historyState, setHistoryState] = useState({ items: [], total: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [sucursales, setSucursales] = useState([]);
  const [stats, setStats] = useState({ total: 0, thisWeek: 0, locations: 0 });
  const [detailState, setDetailState] = useState({ open: false, loading: false, header: null, details: [] });
  const exportContainerRef = useRef(null);

  useLucideIcons([
    viewMode,
    historyState.items.length,
    historyLoading,
    detailState.open,
    filters.sucursal,
    filters.from,
    filters.to
  ]);

  useEffect(() => {
    document.body.classList.add('history-page');
    return () => document.body.classList.remove('history-page');
  }, []);

  useEffect(() => {
    setSearchDraft(filters.query);
  }, [filters.query]);

  const processedRecords = useMemo(() => historyState.items.map((item) => {
    const parsed = parseParametros(item.parametros_json);
    return {
      ...item,
      isManualTime: parsed.isManualTime,
      simulationTime: parsed.simulationTime
    };
  }), [historyState.items]);

  const fetchHistory = useCallback(async (signal) => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      if (filters.sucursal) params.set('sucursal', filters.sucursal);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.query) params.set('q', filters.query.trim());

      const response = await fetch(`/api/forecast_history?${params.toString()}`, { signal });
      if (!response.ok) {
        throw new Error('No se pudieron obtener los registros');
      }
      const data = await response.json();
      if (!signal?.aborted) {
        setHistoryState({ items: data.items || [], total: data.total || 0 });
      }
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      setHistoryError(error.message || 'Error al cargar el historial');
    } finally {
      if (!signal?.aborted) {
        setHistoryLoading(false);
      }
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    const controller = new AbortController();
    fetchHistory(controller.signal);
    return () => controller.abort();
  }, [fetchHistory, refreshToken]);

  useEffect(() => {
    const controller = new AbortController();
    const loadSucursales = async () => {
      try {
        const response = await fetch('/api/get_sucursales', { signal: controller.signal });
        if (!response.ok) {
          throw new Error('Error al cargar sucursales');
        }
        const payload = await response.json();
        if (!controller.signal.aborted && Array.isArray(payload)) {
          setSucursales(payload);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn('No se pudieron obtener sucursales:', error.message);
        }
      }
    };
    loadSucursales();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const loadStats = async () => {
      try {
        const response = await fetch('/api/forecast_history?page=1&page_size=1000', { signal: controller.signal });
        if (!response.ok) {
          throw new Error('No se pudieron obtener estadísticas');
        }
        const data = await response.json();
        if (controller.signal.aborted) {
          return;
        }
        const items = data.items || [];
        const total = data.total || items.length;
        const today = new Date();
        const oneWeekAgo = new Date(today);
        oneWeekAgo.setDate(today.getDate() - 7);
        const thisWeek = items.filter((item) => {
          const createdAt = new Date(item.created_at);
          return createdAt >= oneWeekAgo;
        }).length;
        const locations = new Set(items.map((item) => item.sucursal_nombre).filter(Boolean)).size;
        setStats({ total, thisWeek, locations });
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn('No se pudieron obtener estadísticas:', error.message);
          setStats({ total: 0, thisWeek: 0, locations: 0 });
        }
      }
    };
    loadStats();
    return () => controller.abort();
  }, []);

  const fetchDetail = useCallback(async (recordId) => {
    const response = await fetch(`/api/forecast_history/${recordId}`);
    if (!response.ok) {
      throw new Error('No se pudo obtener el detalle del pronóstico');
    }
    return response.json();
  }, []);

  const handleViewRecord = useCallback(async (recordId) => {
    setDetailState({ open: true, loading: true, header: null, details: [] });
    try {
      const payload = await fetchDetail(recordId);
      setDetailState({ open: true, loading: false, header: payload.header, details: payload.details || [] });
    } catch (error) {
      console.error('Error opening history detail:', error);
      setDetailState({ open: true, loading: false, header: null, details: [] });
    }
  }, [fetchDetail]);

  const ensureDetailData = useCallback(async (recordId) => {
    if (detailState.header && detailState.header.id === recordId && detailState.details.length) {
      return { header: detailState.header, details: detailState.details };
    }
    return fetchDetail(recordId);
  }, [detailState, fetchDetail]);

  const handleExportRecord = useCallback(async (recordId, format) => {
    try {
      const payload = await ensureDetailData(recordId);
      const detailGroups = groupDetailRecords(payload.details || []);
      const verificationUrl = `${window.location.origin}/history/verify?h=${encodeURIComponent(payload.header.id)}&s=${encodeURIComponent(payload.header.snapshot_uuid || '')}`;
      await exportDocumentFile({
        container: exportContainerRef.current,
        header: payload.header,
        detailGroups,
        verificationUrl,
        format
      });
    } catch (error) {
      console.error('Error exporting history record:', error);
    }
  }, [ensureDetailData]);

  const handleFiltersChange = (changes) => {
    setFilters((prev) => ({ ...prev, ...changes }));
    setPage(1);
  };

  const handleResetFilters = () => {
    const defaultRange = createDefaultDateRange();
    setFilters({ sucursal: '', query: '', ...defaultRange });
    setSearchDraft('');
    setPage(1);
  };

  const handlePaginationChange = (nextPage) => {
    setPage(Math.max(1, nextPage));
  };

  const handleRecordAction = (action, recordId) => {
    if (action === 'view') {
      handleViewRecord(recordId);
    } else {
      handleExportRecord(recordId, action === 'pdf' ? 'pdf' : 'jpg');
    }
  };

  const handleRefresh = () => {
    setHistoryError(null);
    setPage(1);
    setRefreshToken((prev) => prev + 1);
  };

  const historyContent = () => {
    if (historyLoading) {
      return (
        <section className="loading-state" id="loadingState">
          <div className="loading-spinner" />
          <div className="loading-text">Consultando registros...</div>
        </section>
      );
    }

    if (historyError) {
      return (
        <section className="empty-state" id="emptyState">
          <div className="empty-icon">
            <Icon name="alert-triangle" size={32} />
          </div>
          <h3>{historyError}</h3>
          <p>Intenta nuevamente o verifica tu conexión.</p>
        </section>
      );
    }

    if (!processedRecords.length) {
      return (
        <section className="empty-state" id="emptyState">
          <div className="empty-icon">
            <Icon name="inbox" size={32} />
          </div>
          <h3>Sin resultados por el momento</h3>
          <p>Intenta ajustando los filtros de búsqueda o revisa un rango de fechas diferente.</p>
        </section>
      );
    }

    return (
      <>
        <HistoryResultsHeader
          viewMode={viewMode}
          onViewChange={setViewMode}
          onRefresh={handleRefresh}
          refreshing={historyLoading}
          totalRecords={historyState.total.toLocaleString()}
        />
        {viewMode === 'cards' ? (
          <HistoryCardsView records={processedRecords} onAction={handleRecordAction} />
        ) : (
          <HistoryTableView records={processedRecords} onAction={handleRecordAction} />
        )}
        <PaginationControls
          page={page}
          total={historyState.total}
          pageSize={pageSize}
          onPageChange={handlePaginationChange}
        />
      </>
    );
  };

  if (!bootstrap?.authenticated) {
    return (
      <div id="history-page" className="history-page">
        <main className="history-main unauthenticated">
          <h1>Inicia sesión para consultar el historial</h1>
          <p>Esta sección es exclusiva para usuarios autorizados.</p>
          <a className="auth-btn login-btn" href={bootstrap?.routes?.login || '/login'}>
            Acceder con Google
          </a>
        </main>
      </div>
    );
  }

  return (
    <div id="history-page" className="history-page">
      <header className="main-header">
        <div className="header-left">
          <a href="/">
            <img src={resolveStaticUrl('images/logo.png')} alt="Tim Hortons" className="header-logo" />
          </a>
          <h1 className="system-title desktop-only">Historial de Pronósticos</h1>
        </div>
        <div className="header-center" />
        <div className="header-right">
          <a href="/" className="header-action-btn admin-btn back-btn">
            <Icon name="arrow-left" size={16} />
            <span>Volver</span>
          </a>
          <AuthSection />
        </div>
      </header>

      <main className="history-main">
        <HistoryHeroStats stats={stats} />
        
        {showDashboard && (
          <section className="filters-panel" style={{ marginBottom: '24px' }}>
            <div className="filters-container">
              <div className="filters-header">
                <h2 className="filters-title">
                  <i data-lucide="layers" width="18" height="18"></i>
                  Vista
                </h2>
              </div>
              <div className="view-toggle" style={{ marginTop: '16px' }}>
                <button
                  className={`toggle-btn ${activeTab === 'historial' ? 'active' : ''}`}
                  onClick={() => setActiveTab('historial')}
                  style={{ flex: 1, padding: '12px 24px' }}
                >
                  <i data-lucide="archive" width="16" height="16"></i>
                  <span>Historial</span>
                </button>
                <button
                  className={`toggle-btn ${activeTab === 'metricas' ? 'active' : ''}`}
                  onClick={() => setActiveTab('metricas')}
                  style={{ flex: 1, padding: '12px 24px' }}
                >
                  <i data-lucide="bar-chart-3" width="16" height="16"></i>
                  <span>Métricas</span>
                </button>
              </div>
            </div>
          </section>
        )}
        
        {activeTab === 'metricas' && showDashboard ? (
          <HistoryMetricsSection
            onNotify={(notification) => pushToast(notification.message, notification.type)}
          />
        ) : (
          <>
            <HistoryFilters
          filters={filters}
          searchDraft={searchDraft}
          sucursales={sucursales}
          onFiltersChange={handleFiltersChange}
          onReset={handleResetFilters}
          onSearchDraftChange={setSearchDraft}
              onSearchApply={() => handleFiltersChange({ query: searchDraft.trim() })}
            />
            <section className="results-section">
              {historyContent()}
            </section>
          </>
        )}
        <div ref={exportContainerRef} id="export-document" style={{ position: 'fixed', top: '-9999px', left: '-9999px' }} />
      </main>

      <HistoryDetailModal
        open={detailState.open}
        loading={detailState.loading}
        header={detailState.header}
        detailGroups={groupDetailRecords(detailState.details)}
        onClose={() => setDetailState((prev) => ({ ...prev, open: false }))}
      />
      
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default HistoryPage;
